import React, { useRef, useEffect, useState, useCallback } from 'react';

interface StickyScrollContainerProps {
  children: React.ReactNode;
  className?: string;
}

const StickyScrollContainer: React.FC<StickyScrollContainerProps> = ({ children, className = '' }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollBarRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [scrollBarPosition, setScrollBarPosition] = useState<'hidden' | 'top' | 'bottom' | 'viewport'>('hidden');
  const scrollSyncInProgress = useRef(false);

  // Check if container is visible in viewport and determine scroll bar position
  const updateScrollBarPosition = useCallback(() => {
    if (!scrollContainerRef.current || !hasOverflow) {
      setScrollBarPosition('hidden');
      return;
    }

    const container = scrollContainerRef.current;
    const rect = container.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Container is above viewport
    if (rect.bottom < 0) {
      setScrollBarPosition('hidden');
      return;
    }

    // Container is below viewport
    if (rect.top > viewportHeight) {
      setScrollBarPosition('hidden');
      return;
    }

    // Container top is visible, bottom is below viewport - show at viewport bottom
    if (rect.top >= 0 && rect.bottom > viewportHeight) {
      setScrollBarPosition('viewport');
      return;
    }

    // Container top is above viewport, bottom is visible - show at viewport bottom
    if (rect.top < 0 && rect.bottom > 0) {
      setScrollBarPosition('viewport');
      return;
    }

    // Entire container is visible - show at container bottom
    if (rect.top >= 0 && rect.bottom <= viewportHeight) {
      setScrollBarPosition('bottom');
      return;
    }

    // Default to bottom
    setScrollBarPosition('bottom');
  }, [hasOverflow]);

  const checkOverflow = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    const scrollBar = scrollBarRef.current;

    if (!scrollContainer || !scrollBar) return;

    const needsScroll = scrollContainer.scrollWidth > scrollContainer.clientWidth;
    setHasOverflow(needsScroll);

    if (needsScroll) {
      scrollBar.style.width = `${scrollContainer.clientWidth}px`;
      const scrollBarContent = scrollBar.firstElementChild as HTMLDivElement;
      if (scrollBarContent) {
        scrollBarContent.style.width = `${scrollContainer.scrollWidth}px`;
      }
    }

    updateScrollBarPosition();
  }, [updateScrollBarPosition]);

  const syncScroll = useCallback((source: HTMLDivElement, target: HTMLDivElement) => {
    if (scrollSyncInProgress.current) return;
    scrollSyncInProgress.current = true;
    target.scrollLeft = source.scrollLeft;
    requestAnimationFrame(() => {
      scrollSyncInProgress.current = false;
    });
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const scrollBar = scrollBarRef.current;

    if (!scrollContainer || !scrollBar) return;

    const handleScrollContainerScroll = () => {
      if (scrollBar) {
        syncScroll(scrollContainer, scrollBar);
      }
    };

    const handleScrollBarScroll = () => {
      if (scrollContainer) {
        syncScroll(scrollBar, scrollContainer);
      }
    };

    const handleScroll = () => {
      updateScrollBarPosition();
    };

    // Initial check
    checkOverflow();

    // Add scroll event listeners
    scrollContainer.addEventListener('scroll', handleScrollContainerScroll, { passive: true });
    scrollBar.addEventListener('scroll', handleScrollBarScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', checkOverflow, { passive: true });

    // Check overflow on resize and mutations
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(scrollContainer);

    const mutationObserver = new MutationObserver(checkOverflow);
    mutationObserver.observe(scrollContainer, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    // Use Intersection Observer to detect visibility
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(() => {
          updateScrollBarPosition();
        });
      },
      {
        threshold: [0, 0.1, 0.5, 0.9, 1],
        rootMargin: '0px 0px -50px 0px',
      }
    );
    intersectionObserver.observe(scrollContainer);

    return () => {
      scrollContainer.removeEventListener('scroll', handleScrollContainerScroll);
      scrollBar.removeEventListener('scroll', handleScrollBarScroll);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkOverflow);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [checkOverflow, syncScroll, updateScrollBarPosition]);

  const getScrollBarStyle = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      height: '16px',
      transition: 'opacity 0.2s ease, transform 0.2s ease',
    };

    switch (scrollBarPosition) {
      case 'hidden':
        return {
          ...baseStyle,
          opacity: 0,
          pointerEvents: 'none',
          position: 'absolute',
          bottom: 0,
        };
      case 'viewport':
        return {
          ...baseStyle,
          position: 'fixed',
          bottom: '10px',
          left: scrollContainerRef.current?.getBoundingClientRect().left || 0,
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        };
      case 'bottom':
        return {
          ...baseStyle,
          position: 'sticky',
          bottom: 0,
          marginTop: '-20px',
        };
      case 'top':
        return {
          ...baseStyle,
          position: 'sticky',
          top: 0,
        };
      default:
        return baseStyle;
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      {/* Main scrollable content */}
      <div
        ref={scrollContainerRef}
        className={`overflow-x-auto ${className}`}
        style={{ paddingBottom: hasOverflow ? '20px' : '0' }}
      >
        {children}
      </div>

      {/* Dynamic horizontal scrollbar */}
      {hasOverflow && scrollBarPosition !== 'hidden' && (
        <div
          ref={scrollBarRef}
          className="overflow-x-auto overflow-y-hidden z-50 bg-white border-t border-gray-300 shadow-sm"
          style={getScrollBarStyle()}
          role="scrollbar"
          aria-label="Horizontal scrollbar"
          aria-orientation="horizontal"
        >
          <div style={{ height: '1px', pointerEvents: 'none' }} />
        </div>
      )}
    </div>
  );
};

export default StickyScrollContainer;
